import { Component, type ErrorInfo, type ReactNode } from "react";
import { Brand } from "./Brand";
import { isStaleChunkError, reloadForStaleChunk } from "../lib/staleChunkRecovery";

type Props = { children: ReactNode };
type State = { error: unknown | null; refreshing: boolean };

/**
 * Covers native dynamic-import failures too (including browsers that do not
 * dispatch Vite's preload event). It deliberately only auto-reloads a stale
 * deploy chunk; application errors retain their normal console diagnostics.
 */
export class StaleChunkBoundary extends Component<Props, State> {
  state: State = { error: null, refreshing: false };

  static getDerivedStateFromError(error: unknown): State {
    return { error, refreshing: false };
  }

  componentDidCatch(error: unknown, _info: ErrorInfo): void {
    if (isStaleChunkError(error) && reloadForStaleChunk(error)) {
      this.setState({ refreshing: true });
    }
  }

  render() {
    const { error, refreshing } = this.state;
    if (!error) return this.props.children;

    const staleChunk = isStaleChunkError(error);
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="text-center max-w-[440px]">
          <Brand />
          <p className="mt-6 font-serif italic text-ink-100 text-lg leading-snug">
            {refreshing
              ? "DreamBook has been updated — refreshing now."
              : staleChunk
                ? "This page needs the latest DreamBook update."
                : "This page could not open."}
          </p>
          <p className="mt-3 text-[13px] text-ink-300 font-ui leading-relaxed">
            {staleChunk
              ? "An open tab requested a page from the version that was just replaced. Refresh to continue safely."
              : "Something unexpected prevented this page from opening. Refresh and try again."}
          </p>
          <div className="mt-7 flex items-center justify-center gap-3">
            <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
              Refresh DreamBook
            </button>
            <a href="/library" className="btn-subtle">Browse the library</a>
          </div>
        </div>
      </div>
    );
  }
}
